import React from 'react'
import PropTypes from 'prop-types'
import ExperienceNavTrigger from './ExperienceNavTrigger'
import NAV_ROW_GLYPHS from './navRowGlyphs'
import navigationItemPropType, { navigationParentPropType } from './navigationItemPropType'
import { NAV_ROW_KINDS } from './navigationModel'
import useNavRowLabels from './useNavRowLabels'
import { EDGE_TAB_PLACEMENT, TOP_CORNER_PLACEMENT, leaveRowCss, rowGlyphCss } from './experienceNavSurface.styles'

// How each kind of row presents on the handle: what it shows, what it is
// called, and what it does - the same glyph, name and handler its card row
// would have had. Experience rows bring their own artwork, wrapped as the
// card wraps it so rowGlyphCss treats both alike.
const ROW_FACES = {
  [NAV_ROW_KINDS.BACK]: ({ backAriaLabel, onBack }) => ({
    glyph: NAV_ROW_GLYPHS[NAV_ROW_KINDS.BACK],
    label: backAriaLabel,
    onSelect: onBack,
  }),
  [NAV_ROW_KINDS.PARENT]: ({ onSelectParent }, _labels, parent) => ({
    glyph: NAV_ROW_GLYPHS[NAV_ROW_KINDS.PARENT],
    label: parent.ariaLabel ?? parent.label,
    onSelect: onSelectParent,
  }),
  [NAV_ROW_KINDS.ITEM]: ({ onSelectNavItem }, _labels, item) => ({
    glyph: <span aria-hidden>{item.icon}</span>,
    label: item.ariaLabel ?? item.label,
    onSelect: () => onSelectNavItem(item),
  }),
  [NAV_ROW_KINDS.HOME]: ({ onHome }, { allExperiencesLabel }) => ({
    glyph: NAV_ROW_GLYPHS[NAV_ROW_KINDS.HOME],
    label: allExperiencesLabel,
    onSelect: onHome,
  }),
  [NAV_ROW_KINDS.KIDS_RETURN]: ({ onKidsReturn }, { kidsReturnLabel }) => ({
    glyph: NAV_ROW_GLYPHS[NAV_ROW_KINDS.KIDS_RETURN],
    label: kidsReturnLabel,
    onSelect: onKidsReturn,
  }),
  [NAV_ROW_KINDS.LEAVE_KIOSK]: ({ onLeaveKiosk }, { leaveKioskLabel }) => ({
    glyph: NAV_ROW_GLYPHS[NAV_ROW_KINDS.LEAVE_KIOSK],
    label: leaveKioskLabel,
    onSelect: onLeaveKiosk,
    isLeave: true,
  }),
}

/**
 * The handle, being the card's one row. A card of one row is a menu with
 * nothing to choose from, so instead of opening it the handle shows that
 * row's glyph, answers to its name, and does what it does - one tap where
 * the card would have cost two and a full-screen overlay. It is not a menu,
 * so it carries none of the popup ARIA the card's handle does.
 *
 * Rendered by SharedNavigationSurface in place of the drawer; see
 * getSoleNavRow for when.
 */
export default function ExperienceNavSoleRowHandle({ row, triggerPlacement, headerVisible, ...handlers }) {
  const labels = useNavRowLabels()
  const { glyph, label, onSelect, isLeave = false } = ROW_FACES[row.kind](handlers, labels, row.item)

  return (
    <ExperienceNavTrigger
      placement={triggerPlacement}
      headerVisible={headerVisible}
      css={[rowGlyphCss, isLeave && leaveRowCss]}
      onClick={onSelect}
      aria-label={label}
    >
      {glyph}
    </ExperienceNavTrigger>
  )
}

ExperienceNavSoleRowHandle.propTypes = {
  /** From getSoleNavRow: which row, and (for a destination or level above) the row itself. */
  row: PropTypes.shape({
    kind: PropTypes.oneOf(Object.values(NAV_ROW_KINDS)).isRequired,
    item: PropTypes.oneOfType([navigationItemPropType, navigationParentPropType]),
  }).isRequired,
  triggerPlacement: PropTypes.oneOf([EDGE_TAB_PLACEMENT, TOP_CORNER_PLACEMENT]).isRequired,
  headerVisible: PropTypes.bool.isRequired,
  backAriaLabel: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onSelectParent: PropTypes.func.isRequired,
  onSelectNavItem: PropTypes.func.isRequired,
  onHome: PropTypes.func.isRequired,
  onKidsReturn: PropTypes.func.isRequired,
  onLeaveKiosk: PropTypes.func.isRequired,
}
